import { defineStore } from "pinia";
import { initializePusher, disconnectPusher } from "@/api/pusher";
import api from "@/api/api";

// Read once at module evaluation so the router guard sees a hydrated store
// on the very first navigation — checkSession() runs after app.mount() which
// is too late to influence the initial route resolution.
function readUserFromStorage() {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem("user") : null;
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
}

export const useAuthStore = defineStore("auth", {
    state: () => ({
        token: typeof localStorage !== "undefined" ? localStorage.getItem("token") : null,
        user: readUserFromStorage(),
        activeWorkspace: typeof localStorage !== "undefined" ? localStorage.getItem("activeWorkspace") : null,
        activeShop: typeof localStorage !== "undefined" ? localStorage.getItem("activeShop") : null,
        shops: [], // lista sklepów dla aktywnego workspace
        shopsLoading: false,
        // Obietnica pierwszego pobrania sklepów — router czeka na nią, zanim
        // uzna brak aktywnego sklepu za powód do odesłania na listę.
        shopsReady: null,
    }),

    actions: {
        login(token, user) {
            this.token = token;
            this.user = typeof user === "string" ? JSON.parse(user) : user;

            // Preload shops if included in login response
            if (Array.isArray(this.user?.shops)) {
                this.shops = this.user.shops;
            }

            localStorage.setItem("token", token);
            localStorage.setItem("user", JSON.stringify(this.user));

            this.setDefaultWorkspaceIfMissing();
            initializePusher(this.user.uuid);

            // Payload logowania niesie sklepy, więc kontekst da się ustalić od
            // razu — bez tego pierwsze przejście routera nie miało wybranego
            // sklepu i odsyłało zaproszonego na listę zamiast do jego sklepu.
            this.restoreShopContext();
            this.shopsReady = Promise.resolve();
        },

        async logout() {
            disconnectPusher();

            this.token = null;
            this.user = null;
            this.activeWorkspace = null;
            this.activeShop = null;
            this.shops = [];
            this.workspaceMemberships = {};
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("activeWorkspace");
            localStorage.removeItem("activeShop");
        },

        addWorkspaceLocal(ws) {
            if (!ws || !ws.uuid) return;
            const name = ws.name || `Workspace ${ws.uuid.slice(0, 6)}`;

            const current = (this.user?.workspaces) || {};
            const next = { ...current, [ws.uuid]: name };

            this.user = { ...(this.user || {}), workspaces: next };
            localStorage.setItem("user", JSON.stringify(this.user));

            if (!this.activeWorkspace) {
                this.setActiveWorkspace(ws.uuid);
            }
        },

        getToken() {
            return this.token || localStorage.getItem("token");
        },

        loadToken() {
            this.token = localStorage.getItem("token");
        },

        checkSession() {
            const storedToken = localStorage.getItem("token");
            const storedUser = localStorage.getItem("user");
            const storedWorkspace = localStorage.getItem("activeWorkspace");
            const storedShop = localStorage.getItem("activeShop");

            if (storedToken && storedUser) {
                this.token = storedToken;
                this.user = JSON.parse(storedUser);
                this.activeWorkspace = storedWorkspace || null;
                this.activeShop = storedShop || null;
                this.setDefaultWorkspaceIfMissing();
                initializePusher(this.user.uuid);

                // Zawsze, nie tylko przy aktywnym workspace: dostęp bywa nadany
                // wprost do sklepu i wtedy workspace'u nie ma. Bez tego sklepy
                // nie doczytywały się przy starcie, aktywnego sklepu nie było,
                // a router odsyłał na listę sklepów workspace'u — pustą.
                this.shopsReady = this.fetchShops();
            }
        },

        getUser() {
            if (typeof this.user === "string") {
                try {
                    this.user = JSON.parse(this.user);
                } catch {
                    this.user = null;
                }
            }
            return this.user;
        },

        setActiveWorkspace(uuid) {
            // No-op when the workspace is unchanged — otherwise we'd wipe the
            // currently active shop on every router refresh that re-asserts
            // the same workspace, sending the user back to the shop picker.
            if (this.activeWorkspace === uuid) return;

            this.activeWorkspace = uuid || null;
            if (uuid) localStorage.setItem("activeWorkspace", uuid);
            else localStorage.removeItem("activeWorkspace");

            // Reset shop when workspace genuinely changes and fetch new shops.
            this.activeShop = null;
            this.shops = [];
            localStorage.removeItem("activeShop");
            if (uuid) {
                this.fetchShops();
            }
        },

        getActiveWorkspace() {
            if (!this.getToken()) return null; // brak sesji
            if (!this.activeWorkspace) {
                const stored = localStorage.getItem("activeWorkspace");
                if (stored) {
                    this.activeWorkspace = stored;
                } else if (this.user?.workspaces) {
                    this.setDefaultWorkspaceIfMissing();
                }
            }
            return this.activeWorkspace;
        },

        hasActiveWorkspace() {
            return !!this.getActiveWorkspace();
        },

        // Dostęp bywa nadany wprost do sklepu, bez workspace'u — o tym, czy panel
        // ma co pokazywać, decyduje sklep, nie członkostwo w organizacji.
        hasActiveShop() {
            return !!this.getActiveShop();
        },

        isAdminOfActiveWorkspace() {
            const ws = this.getActiveWorkspace();
            const map = this.user?.workspaces || {};
            return !!(ws && Object.prototype.hasOwnProperty.call(map, ws));
        },

        getWorkspaceName(uuid) {
            const entry = this.user?.workspaces?.[uuid];
            if (!entry) return null;
            return typeof entry === 'object' ? entry.name : entry;
        },

        // Czy użytkownik należy do workspace'u. Sklep nadany wprost wskazuje na
        // workspace, którego właściciel go zaprosił — a do którego zaproszony nie
        // ma dostępu, więc nie wolno tego workspace'u nigdzie przyjmować.
        belongsToWorkspace(uuid) {
            const map = this.user?.workspaces || {};
            return !!(uuid && Object.prototype.hasOwnProperty.call(map, uuid));
        },

        setDefaultWorkspaceIfMissing() {
            if (!this.user || !this.user.workspaces) return;

            // Zapamiętany workspace mógł zostać po innej sesji albo po dawnym
            // błędzie — trzymanie cudzego kończy się 403 przy pierwszym żądaniu
            // workspace'owym i ekranem „Nie udało się załadować sklepów".
            if (this.activeWorkspace && !this.belongsToWorkspace(this.activeWorkspace)) {
                this.activeWorkspace = null;
                localStorage.removeItem("activeWorkspace");
            }

            if (!this.activeWorkspace) {
                const firstWorkspace = Object.keys(this.user.workspaces)[0];
                if (firstWorkspace) {
                    this.activeWorkspace = firstWorkspace;
                    localStorage.setItem("activeWorkspace", firstWorkspace);
                }
            }
        },

        // ========== Shop management ==========

        setActiveShop(uuid) {
            this.activeShop = uuid || null;
            if (uuid) localStorage.setItem("activeShop", uuid);
            else localStorage.removeItem("activeShop");

            // Workspace idzie za sklepem — ustawiany wprost, żeby nie przejść
            // przez setActiveWorkspace(), które czyści wybrany sklep. Tylko taki,
            // do którego użytkownik faktycznie należy: przy dostępie nadanym
            // wprost do sklepu przyjęcie cudzego workspace'u włączało w panelu
            // widoki workspace'owe, które i tak kończyły się na 403.
            const shop = uuid ? this.shops.find(s => s.uuid === uuid) : null;
            const wsUuid = shop?.workspace_uuid;
            if (wsUuid && wsUuid !== this.activeWorkspace && this.belongsToWorkspace(wsUuid)) {
                this.activeWorkspace = wsUuid;
                localStorage.setItem("activeWorkspace", wsUuid);
            }
        },

        getActiveShop() {
            if (!this.getToken()) return null;
            if (!this.activeShop) {
                const stored = localStorage.getItem("activeShop");
                if (stored) {
                    this.activeShop = stored;
                }
            }
            return this.activeShop;
        },

        async fetchShops() {
            if (!this.getToken()) {
                this.shops = [];
                return;
            }

            this.shopsLoading = true;
            try {
                // Wszystkie sklepy, do których user ma dostęp — także nadane
                // bezpośrednio, poza jego workspace'ami. Pytanie o sklepy
                // jednego workspace wrzucało takich userów do pustej listy.
                const { data } = await api.get(`/api/me/shops`);
                this.shops = data?.data || [];
                this.restoreShopContext();
            } catch (e) {
                console.error("Failed to fetch shops:", e);
                this.shops = [];
            } finally {
                this.shopsLoading = false;
            }
        },

        /**
         * Wybór sklepu po zalogowaniu/odświeżeniu.
         *
         * Kolejność: ostatnio używany sklep, potem — gdy user ma dostęp tylko
         * do jednego — ten jeden. Przy wielu sklepach nie zgadujemy: user
         * wybiera sam. Workspace zawsze wynika z wybranego sklepu, inaczej
         * dostęp nadany bezpośrednio do sklepu lądował w cudzym (pustym)
         * workspace.
         */
        restoreShopContext() {
            const remembered = this.activeShop || localStorage.getItem("activeShop");
            let target = this.shops.find(s => s.uuid === remembered) || null;

            if (!target) {
                // Po ręcznej zmianie workspace szukaj najpierw w nim — inaczej
                // autowybór cofałby użytkownika do sklepu z poprzedniego.
                const scoped = this.activeWorkspace
                    ? this.shops.filter(s => s.workspace_uuid === this.activeWorkspace)
                    : [];
                const pool = scoped.length ? scoped : this.shops;
                target = pool.length === 1 ? pool[0] : null;
            }

            if (!target) {
                this.setActiveShop(null);
                return;
            }

            this.setActiveShop(target.uuid);
        },

        getActiveShopName() {
            if (!this.activeShop) return null;
            const shop = this.shops.find(s => s.uuid === this.activeShop);
            return shop?.name || null;
        },

        getActiveShopData() {
            if (!this.activeShop) return null;
            return this.shops.find(s => s.uuid === this.activeShop) || null;
        },

        isModuleEnabled(moduleName) {
            const shop = this.getActiveShopData();
            if (!shop) return false;
            return shop[moduleName] ?? false;
        },

        updateShopData(shopUuid, updates) {
            const index = this.shops.findIndex(s => s.uuid === shopUuid);
            if (index !== -1) {
                this.shops[index] = { ...this.shops[index], ...updates };
            }
        },

        getActiveShopChannels() {
            const shop = this.getActiveShopData();
            return shop?.channels || [];
        },

        getActiveShopDefaultChannel() {
            const channels = this.getActiveShopChannels();
            return channels.find(c => c.is_default) || channels[0] || null;
        },

        // ========== SSO ==========

        isSsoEnabled() {
            return !!process.env.VUE_APP_SSO_URL;
        },

        /**
         * Ścieżka autoryzacji OAuth w obrębie SSO (bez hosta) — zapisuje przy okazji
         * code_verifier. Jako ścieżka względna nadaje się na `redirect`, którym SSO
         * wraca do panelu po ustawieniu hasła przez zaproszonego.
         */
        async buildSsoAuthorizePath(emailHint = null) {
            const clientId = process.env.VUE_APP_SSO_CLIENT_ID;
            const redirectUri = process.env.VUE_APP_SSO_REDIRECT_URI;

            if (!clientId) return null;

            const codeVerifier = generateCodeVerifier();
            sessionStorage.setItem("sso_code_verifier", codeVerifier);

            const challenge = await generateCodeChallenge(codeVerifier);
            sessionStorage.setItem("sso_code_challenge", challenge);

            const params = new URLSearchParams({
                client_id: clientId,
                redirect_uri: redirectUri,
                response_type: "code",
                scope: "",
                code_challenge: challenge,
                code_challenge_method: "S256",
            });

            if (emailHint) {
                params.set("email_hint", btoa(emailHint));
            }

            return `/oauth/authorize?${params.toString()}`;
        },

        async redirectToSso(emailHint = null) {
            const ssoUrl = process.env.VUE_APP_SSO_URL;

            if (!ssoUrl || !process.env.VUE_APP_SSO_CLIENT_ID) {
                console.warn("[auth] SSO not configured (VUE_APP_SSO_URL / VUE_APP_SSO_CLIENT_ID missing)");
                return;
            }

            const path = await this.buildSsoAuthorizePath(emailHint);
            if (path) window.location.href = `${ssoUrl}${path}`;
        }
    },
});

// PKCE helpers
function generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64URLEncode(array);
}

async function generateCodeChallenge(verifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest("SHA-256", data);
    return base64URLEncode(new Uint8Array(digest));
}

function base64URLEncode(buffer) {
    let str = "";
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i++) {
        str += String.fromCharCode(bytes[i]);
    }
    return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
